import { Check, ChevronsUpDown, Network } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { SidebarMenuButton } from '@/components/sv/sidebar'
import { BreadcrumbItem } from '@/components/ui/breadcrumb'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery, useIdCompany } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  LoadBalancerSwitcherQuery,
  LoadBalancerSwitcherQueryVariables,
} from '@/components/layout/breadcrumbs/load-balancer/load-balancer-switcher/switcher.types.gen'

const LOAD_BALANCER_SWITCHER_QUERY = gql`
  query LoadBalancerSwitcher($idCompany: String!) {
    company(id: $idCompany) {
      id
      edgeLoadBalancers @live {
        id
        total
        items {
          id
          name
          displayName
        }
      }
    }
  }
`

export const LoadBalancerSwitcherData = () => {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const { loadBalancerName = '' } = useParams()
  const { idCompany } = useIdCompany()

  const { data } = useSuspenseQuery<
    LoadBalancerSwitcherQuery,
    LoadBalancerSwitcherQueryVariables
  >(LOAD_BALANCER_SWITCHER_QUERY, {
    variables: { idCompany },
  })

  const loadBalancers = data?.company?.edgeLoadBalancers?.items ?? []

  const activeLoadBalancer =
    loadBalancers.find(
      (loadBalancer) => loadBalancer.name === loadBalancerName
    ) || loadBalancers[0]

  return (
    <BreadcrumbItem>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className="cursor-pointer">
          <SidebarMenuButton
            size="lg"
            aria-expanded={open}
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer"
          >
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span
                title={activeLoadBalancer?.displayName ?? ''}
                className="max-w-[250px] truncate text-foreground font-medium"
              >
                {activeLoadBalancer?.displayName ?? ''}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto" />
          </SidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent
          className="w-max min-w-[max(250px,var(--radix-popper-anchor-width))] max-w-[480px] p-0"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <Command>
            <CommandInput
              placeholder="Search load balancer..."
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>No load balancer found.</CommandEmpty>
              <CommandGroup>
                {loadBalancers.map((loadBalancer) => (
                  <Link
                    key={loadBalancer.id}
                    to={`${pathname.replace(loadBalancerName, loadBalancer.name)}${pathname.endsWith('/') ? '' : '/'}?idCompany=${idCompany}`}
                    onClick={() => setOpen(false)}
                  >
                    <CommandItem
                      value={loadBalancer.name}
                      keywords={[loadBalancer.displayName]}
                      className="cursor-pointer"
                    >
                      <div className="flex size-6 items-center justify-center rounded-md border">
                        {activeLoadBalancer?.name === loadBalancer.name ? (
                          <Check className="size-3.5 shrink-0" />
                        ) : (
                          <Network className="size-3.5 shrink-0" />
                        )}
                      </div>
                      <span
                        className={cn('truncate', {
                          'font-medium':
                            activeLoadBalancer?.name === loadBalancer?.name,
                        })}
                      >
                        {loadBalancer.displayName}
                      </span>
                    </CommandItem>
                  </Link>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </BreadcrumbItem>
  )
}
